import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './hooks/useAuth';
import { useFirebaseCharacter } from './hooks/useFirebaseCharacter';
import { initializeClassProviders } from './providers';
import AuthSection from './components/Auth/AuthSection';
import Navigation from './components/Navigation/Navigation';
import Header from './components/Header/Header';
import Footer from './components/Common/Footer';
import CharacterSheet from './components/CharacterSheet/CharacterSheet';
import CombatTracker from './components/CombatTracker/CombatTracker';
import CharacterSelect from './components/CharacterSelect/CharacterSelect';
import ContentLibrary from './components/ContentLibrary/ContentLibrary';
import DeveloperPage from './components/Developer/DeveloperPage';
import BiographyPage from './pages/BiographyPage';
import LegalPage from './pages/LegalPage';
import { SectionOrderProvider } from './contexts/SectionOrderContext';
import Button from './components/Common/Button';
import { CharacterCreationData } from './components/CharacterCreation/CharacterCreationWizard';
import { findClassById } from './utils/classDataLoader';
import { InventoryItem, EquipmentReference } from './types/character';
import { ROUTES } from './utils/constants';
import {
  findDomainCard,
  findEquipment
} from './utils/contentLibraryHelpers';
import { showToast } from './utils/toast';
import { itemLoader } from './utils/itemLoader';
import { isConsumableItem } from './types/equipment';
import { ThemeProvider } from './theme/providers/ThemeProvider';
import { availableThemes } from './theme/themes';
import './styles/globals.css';
import './styles/components/buttons.css';
import './styles/components/cards.css';
import './styles/components/forms.css';
import './styles/components/index.css';
import './styles/components/layout.css';
import './styles/components/modals.css';
import './styles/responsive.css';
import './styles/navigation.css';
import './styles/theme-toggle.css';
import './styles/theme-transitions.css';

// Helper functions for character persistence
const getStoredCharacterId = (userId: string): string | null => {
  try {
    const stored = localStorage.getItem(`daggerheart_current_character_${userId}`);
    return stored;
  } catch (error) {
    console.warn('Failed to retrieve stored character ID:', error);
    return null;
  }
};

const setStoredCharacterId = (userId: string, characterId: string | null): void => {
  try {
    if (characterId) {
      localStorage.setItem(`daggerheart_current_character_${userId}`, characterId);
    } else {
      localStorage.removeItem(`daggerheart_current_character_${userId}`);
    }
  } catch (error) {
    console.warn('Failed to store character ID:', error);
  }
};

// Inner component that can use React Router hooks
const AppContent: React.FC<{ user: any; authLoading: boolean }> = ({ user, authLoading }) => {
  const navigate = useNavigate();
  const [currentCharacterId, setCurrentCharacterId] = useState<string | null>(null);

  // Load stored character ID on component mount
  useEffect(() => {
    if (user?.uid) {
      const storedCharacterId = getStoredCharacterId(user.uid);
      if (storedCharacterId) {
        setCurrentCharacterId(storedCharacterId);
        
        // If we're on the character select route and have a stored character, navigate to character sheet
        if (window.location.pathname === ROUTES.CHARACTER_SELECT) {
          navigate(ROUTES.CHARACTER_SHEET);
        }
      }
    }
  }, [user?.uid, navigate]);

  // Update stored character ID when current character changes
  useEffect(() => {
    if (user?.uid) {
      setStoredCharacterId(user.uid, currentCharacterId);
    }
  }, [user?.uid, currentCharacterId]);

  // Initialize class providers on app startup
  useEffect(() => {
    initializeClassProviders();
  }, []);
  
  // Enhanced character system - Optimistic updates
  const enhancedCharacter = useFirebaseCharacter({
    userId: user?.uid || '',
    characterId: currentCharacterId || undefined,
    autoSave: true,
    autoSubscribe: true,
  });
  
  // Extract enhanced character API
  const {
    character,
    baseCharacter, // For duplicate detection
    loading: characterLoading,
    computedStats,
    statComputer,
    activateClassFeature,
    deactivateClassFeature,
    createCharacter,
    updateCharacterInfo,
    updateBaseStats,
    updateCurrentState,
    updateManualOverrides,
    updateExperiences,
    updateInventoryManualItems,
    updateInventoryDomainCards,
    updateBiography,
    updateMarkedTraits,
    updateTierAdvancements,
    updateLevelUpHistory,
    updateMulticlass
  } = enhancedCharacter;

  // Auto-save control state
  const [, setAutoSaveDisabled] = React.useState(false);
  
  // Save state for Firebase integration
  const saveState = React.useMemo(() => {
    const saving = enhancedCharacter.saving;
    const hasUnsavedChanges = enhancedCharacter.hasUnsavedChanges;
    const error = enhancedCharacter.error;
    
    if (error) return { status: 'error' as const };
    if (saving) return { status: 'saving' as const };
    if (hasUnsavedChanges) return { status: 'modified' as const };
    return { status: 'saved' as const };
  }, [enhancedCharacter.saving, enhancedCharacter.hasUnsavedChanges, enhancedCharacter.error]);
  
  const forceSave = React.useCallback(async () => {
    return enhancedCharacter.saveCharacter();
  }, [enhancedCharacter]);
  
  // Auto-save control functions
  const disableAutoSave = React.useCallback(() => {
    setAutoSaveDisabled(true);
  }, []);
  
  const enableAutoSave = React.useCallback(() => {
    setAutoSaveDisabled(false);
  }, []);

  const handleCreateCharacter = React.useCallback(async (characterData?: CharacterCreationData) => {
    try {
      let initialData: any = {};
      
      if (characterData) {
        // Load class data to get starting combat stats
        let classData: any = null;
        if (characterData.class) {
          try {
            classData = await findClassById(characterData.class);
          } catch (error) {
            console.warn('Could not load class data for starting stats:', error);
          }
        }
        
        // Convert wizard data to enhanced character format
        initialData = {
          name: characterData.name,
          class: characterData.class,
          heritage: characterData.ancestry,
          community: characterData.community,
          subclass: characterData.subclass,
          level: '1',
          tier: 1,
          proficiency: 1, // All characters start at proficiency 1
          // Preserve biographical data (Phase 49)
          biography: {
            appearance: characterData.appearance,
            backgroundAnswers: characterData.backgroundAnswers
              ? characterData.backgroundAnswers.filter(item => item.answer.trim())
              : undefined,
            connectionAnswers: characterData.connectionAnswers
              ? characterData.connectionAnswers.filter(item => item.answer.trim())
              : undefined,
            startingChoices: characterData.startingChoices ? Object.entries(characterData.startingChoices).map(([category, choice]) => ({
              category,
              choice: choice as string
            })) : undefined
          },
          // CRITICAL: Always initialize baseStats with complete structure to prevent stat computation errors
          baseStats: {
            traits: {
              agility: 0,
              strength: 0,
              finesse: 0,
              instinct: 0,
              presence: 0,
              knowledge: 0,
            },
            evasion: 0,
            armor: 0,
            hpSlots: 0,
            stressSlots: 0,
            hope: 6,
            thresholds: {
              major: 1,
              severe: 2,
            }
          }
        };

        // Set starting combat stats from class data
        if (classData) {
          initialData.maxHitPoints = classData.startingHitPoints;
          initialData.maxStress = classData.startingStress;
          initialData.baseEvasion = classData.startingEvasion;

          // Initialize combat state with max values
          initialData.currentHope = 2; // Start with 2 Hope (Daggerheart default)
          initialData.hpMarked = 0; // Start with full HP
          initialData.stressMarked = 0; // Start with no stress
          initialData.armorMarked = 0; // Start with full armor

          // Update baseStats with class-specific values
          initialData.baseStats.evasion = classData.startingEvasion || 0;
          initialData.baseStats.hpSlots = classData.startingHitPoints || 0;
          initialData.baseStats.stressSlots = classData.startingStress || 0;
        }

        // Apply user-selected traits from wizard (Phase 61)
        // If user selected custom traits, use those; otherwise fall back to class suggested traits
        if (characterData.traits) {
          initialData.baseStats.traits = {
            agility: characterData.traits.agility,
            strength: characterData.traits.strength,
            finesse: characterData.traits.finesse,
            instinct: characterData.traits.instinct,
            presence: characterData.traits.presence,
            knowledge: characterData.traits.knowledge,
          };
          // Also set the flattened traits property for compatibility
          initialData.traits = { ...characterData.traits };
        } else if (classData?.suggestedTraits) {
          // Fall back to class suggested traits if user didn't assign custom ones
          initialData.baseStats.traits = {
            agility: classData.suggestedTraits.agility,
            strength: classData.suggestedTraits.strength,
            finesse: classData.suggestedTraits.finesse,
            instinct: classData.suggestedTraits.instinct,
            presence: classData.suggestedTraits.presence,
            knowledge: classData.suggestedTraits.knowledge,
          };
          initialData.traits = { ...classData.suggestedTraits };
        }

        // Save user-created experiences (Phase 62)
        if (characterData.experiences && characterData.experiences.length > 0) {
          initialData.experiences = characterData.experiences;
        }

        // Add selected starting equipment to inventory
        if (characterData.selectedEquipment && classData?.suggestedEquipment) {
          const equipmentReferences: EquipmentReference[] = [];

          // Add primary weapon if selected
          if (characterData.selectedEquipment.primaryWeapon && classData.suggestedEquipment.primaryWeapon) {
            equipmentReferences.push({
              itemId: classData.suggestedEquipment.primaryWeapon,
              quantity: 1,
              equippedCount: 0
            });
          }

          // Add secondary weapon if selected
          if (characterData.selectedEquipment.secondaryWeapon && classData.suggestedEquipment.secondaryWeapon) {
            equipmentReferences.push({
              itemId: classData.suggestedEquipment.secondaryWeapon,
              quantity: 1,
              equippedCount: 0
            });
          }

          // Add armor if selected
          if (characterData.selectedEquipment.armor && classData.suggestedEquipment.armor) {
            equipmentReferences.push({
              itemId: classData.suggestedEquipment.armor,
              quantity: 1,
              equippedCount: 0
            });
          }

          // Add universal starting items to manual inventory (per character_creation_rules.md)
          const manualItems: InventoryItem[] = [];
          const universalStartingItems = [
            'A torch',
            '50 feet of rope',
            'Basic supplies',
            'A handful of gold'
          ];
          universalStartingItems.forEach((item: string) => {
            manualItems.push({
              name: item,
              description: '',
              quantity: 1
            });
          });

          // Initialize inventory with starting items
          if (equipmentReferences.length > 0 || manualItems.length > 0) {
            initialData.inventory = {
              equipmentReferences: equipmentReferences,
              domainCardVault: [],
              domainCardLoadout: characterData.domainCards || [], // Add user-selected domain cards to loadout (Phase 63)
              manualItems: manualItems
            };
          }
        } else if (characterData.domainCards && characterData.domainCards.length > 0) {
          // Even if no equipment is selected, ensure domain cards and universal starting items are saved
          const universalItems = [
            { name: 'A torch', description: '', quantity: 1 },
            { name: '50 feet of rope', description: '', quantity: 1 },
            { name: 'Basic supplies', description: '', quantity: 1 },
            { name: 'A handful of gold', description: '', quantity: 1 }
          ];
          initialData.inventory = {
            equipmentReferences: [],
            domainCardVault: [],
            domainCardLoadout: characterData.domainCards,
            manualItems: universalItems
          };
        }
      }
      
      // Create character and get the actual Firebase-generated ID
      const characterId = await createCharacter(initialData);
      // Set the current character as active so the app shows the character sheet
      setCurrentCharacterId(characterId);
      return characterId;
    } catch (error) {
      console.error('Failed to create character:', error);
      throw error;
    }
  }, [createCharacter]);

  // Helper function to check for duplicates using base character state (updated for vault/loadout system)
  const isDuplicateDomainCard = React.useCallback((cardId: string): boolean => {
    const inVault = baseCharacter?.inventory?.domainCardVault?.some(ref => ref.contentId === cardId) || false;
    const inLoadout = baseCharacter?.inventory?.domainCardLoadout?.some(ref => ref.contentId === cardId) || false;
    return inVault || inLoadout;
  }, [baseCharacter]);

  // Handle character selection navigation
  const handleSelectCharacterAndNavigate = React.useCallback((characterId: string) => {
    setCurrentCharacterId(characterId);
    navigate(ROUTES.CHARACTER_SHEET);
  }, [navigate]);

  const handleCreateCharacterAndNavigate = React.useCallback(async (characterData?: CharacterCreationData) => {
    try {
      const characterId = await handleCreateCharacter(characterData);
      navigate(ROUTES.CHARACTER_SHEET);
      return characterId;
    } catch (error) {
      console.error('Failed to create character:', error);
      throw error;
    }
  }, [handleCreateCharacter, navigate]);

  const handleNavigateToCharacterSelect = React.useCallback(async () => {
    // Save any unsaved changes before navigating
    if (saveState?.status === 'modified') {
      try {
        await forceSave();
        showToast.autoSave.success();
      } catch (error) {
        showToast.autoSave.error('Failed to save before navigation');
        // Continue navigation even if save fails - user can manually save later
      }
    }
    
    // Clear current character and navigate to character select
    setCurrentCharacterId(null);
    navigate(ROUTES.CHARACTER_SELECT);
  }, [navigate, saveState, forceSave]);
  
  // Keyboard shortcut for saving (Ctrl+S)
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        if (saveState?.status === 'modified') {
          forceSave();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [forceSave, saveState]);

  // Legacy update function for components that haven't been migrated yet
  const updateCharacterData = (updates: any) => {
    // Check if this is a complete CharacterData object (from level-up wizard)
    // Complete objects have an 'id' field and baseStats object
    const isCompleteCharacter = updates.id && updates.baseStats;

    if (isCompleteCharacter) {
      // Handle complete character replacement from level-up wizard
      console.log('[App] Applying complete character update from level-up');

      // Update basic info (only fields supported by updateCharacterInfo)
      updateCharacterInfo({
        ...(updates.name && { name: updates.name }),
        ...(updates.class && { class: updates.class }),
        ...(updates.heritage && { heritage: updates.heritage }),
        ...(updates.level && { level: updates.level }),
        ...(updates.tier && { tier: updates.tier }),
        ...(updates.subclass && { subclass: updates.subclass }),
        ...(updates.subclassCardTier && { subclassCardTier: updates.subclassCardTier }),
        ...(updates.proficiency !== undefined && { proficiency: updates.proficiency }),
      });

      // Update base stats (includes traits, armor, evasion, hope, thresholds, etc.)
      if (updates.baseStats) {
        updateBaseStats(updates.baseStats);
      }

      // Update current state (HP, stress, armor marked, etc.)
      if (updates.currentState) {
        updateCurrentState(updates.currentState);
      }

      // Update experiences
      if (updates.experiences) {
        updateExperiences(updates.experiences);
      }

      // Update inventory if present
      if (updates.inventory) {
        updateInventoryManualItems(updates.inventory.manualItems || []);
        updateInventoryDomainCards(
          updates.inventory.domainCardLoadout || [],
          updates.inventory.domainCardVault || []
        );
      }

      // Update biography if present
      if (updates.biography) {
        updateBiography(updates.biography);
      }

      // Update level-up system fields (Phase 26A)
      if (updates.markedTraits) {
        updateMarkedTraits(updates.markedTraits);
      }

      if (updates.tierAdvancements) {
        updateTierAdvancements(updates.tierAdvancements);
      }

      if (updates.levelUpHistory) {
        updateLevelUpHistory(updates.levelUpHistory);
      }

      // Update multiclass (Phase 26B)
      if (updates.multiclass !== undefined) {
        updateMulticlass(updates.multiclass);
      }

      return;
    }

    // Handle partial updates (legacy behavior)
    if (updates.name || updates.class || updates.heritage || updates.subclass || updates.community || updates.level || updates.tier || updates.subclassCardTier) {
      updateCharacterInfo({
        ...(updates.name && { name: updates.name }),
        ...(updates.class && { class: updates.class }),
        ...(updates.heritage && { heritage: updates.heritage }),
        ...(updates.level && { level: updates.level }),
        ...(updates.tier && { tier: updates.tier }),
        ...(updates.subclass && { subclass: updates.subclass }),
        ...(updates.subclassCardTier && { subclassCardTier: updates.subclassCardTier }),
      });
    }

    if (updates.traits) {
      updateBaseStats({
        traits: {
          agility: parseInt(updates.traits.agility || '0', 10),
          strength: parseInt(updates.traits.strength || '0', 10),
          finesse: parseInt(updates.traits.finesse || '0', 10),
          instinct: parseInt(updates.traits.instinct || '0', 10),
          presence: parseInt(updates.traits.presence || '0', 10),
          knowledge: parseInt(updates.traits.knowledge || '0', 10),
        }
      });
    }

    if (updates.currentHope !== undefined || updates.hpMarked || updates.stressMarked || updates.armorMarked) {
      updateCurrentState({
        ...(updates.currentHope !== undefined && { currentHope: updates.currentHope }),
        ...(updates.hpMarked && { hpMarked: updates.hpMarked }),
        ...(updates.stressMarked && { stressMarked: updates.stressMarked }),
        ...(updates.armorMarked && { armorMarked: updates.armorMarked }),
      });
    }

    if (updates.experiences) {
      updateExperiences(updates.experiences);
    }

    if (updates.inventory) {
      updateInventoryManualItems(updates.inventory.manualItems || []);
    }

    if (updates.biography) {
      updateBiography(updates.biography);
    }
  };

  const createEnhancedCharacterApi = () => ({
    character,
    computedStats,
    statComputer,
    activateClassFeature,
    deactivateClassFeature,
    // Direct character update methods
    updateCharacterInfo,
    updateBaseStats,
    updateCurrentState,
    updateManualOverrides,
    updateExperiences,
    updateInventoryManualItems,
    updateInventoryDomainCards,
    updateBiography,
    // Reference-based equipment operations
    addEquipmentByReference: enhancedCharacter.addEquipmentByReference,
    removeEquipmentByReference: enhancedCharacter.removeEquipmentByReference,
    equipItemByReference: enhancedCharacter.equipItemByReference,
    unequipItemByReference: enhancedCharacter.unequipItemByReference,
    // Loot & Consumable management (Phase 2)
    addLootByReference: enhancedCharacter.addLootByReference,
    removeLootByReference: enhancedCharacter.removeLootByReference,
    updateLootQuantity: enhancedCharacter.updateLootQuantity,
    addConsumableByReference: enhancedCharacter.addConsumableByReference,
    removeConsumableByReference: enhancedCharacter.removeConsumableByReference,
    updateConsumableQuantity: enhancedCharacter.updateConsumableQuantity,
    // Vault/loadout domain card operations (NEW)
    addDomainCardToLoadout: enhancedCharacter.addDomainCardToLoadout,
    moveDomainCardToLoadout: enhancedCharacter.moveDomainCardToLoadout,
    moveDomainCardToVault: enhancedCharacter.moveDomainCardToVault,
    removeDomainCard: enhancedCharacter.removeDomainCard,
  });

  if (authLoading) {
    return (
      <div className="app">
        <div className="app__loading">
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="app">
        <Header />
        <div className="app__auth">
          <AuthSection />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app">
      <Header
        saveState={saveState}
        onSave={forceSave}
        onNavigateToCharacterSelect={handleNavigateToCharacterSelect}
        showCharacterSelect={!!currentCharacterId}
      />

      <main className="app__main">
        <Routes>
          <Route
            path={ROUTES.CHARACTER_SELECT}
            element={
              <CharacterSelect
                userId={user.uid}
                onSelectCharacter={handleSelectCharacterAndNavigate}
                onCreateCharacter={handleCreateCharacterAndNavigate}
              />
            }
          />
          <Route
            path={ROUTES.CHARACTER_SHEET}
            element={
              characterLoading ? (
                <div className="app__loading">
                  Loading character...
                </div>
              ) : currentCharacterId && character ? (
                <>
                  <Navigation />
                  <SectionOrderProvider userId={user.uid}>
                    <CharacterSheet
                      enhancedCharacter={createEnhancedCharacterApi()}
                      updateCharacterData={updateCharacterData}
                      disableAutoSave={disableAutoSave}
                      enableAutoSave={enableAutoSave}
                    />
                  </SectionOrderProvider>
                </>
              ) : (
                <>
                  <Navigation />
                  <div className="app__error">
                    <p>No character selected. Please select a character first.</p>
                    <Button onClick={handleNavigateToCharacterSelect}>
                      Go to Character Select
                    </Button>
                  </div>
                </>
              )
            }
          />
          <Route
            path={ROUTES.COMBAT_TRACKER}
            element={
              characterLoading ? (
                <div className="app__loading">
                  Loading character...
                </div>
              ) : currentCharacterId && character ? (
                <>
                  <Navigation />
                  <CombatTracker
                    enhancedCharacter={createEnhancedCharacterApi()}
                  />
                </>
              ) : (
                <>
                  <Navigation />
                  <div className="app__error">
                    <p>No character selected. Please select a character first.</p>
                    <Button onClick={handleNavigateToCharacterSelect}>
                      Go to Character Select
                    </Button>
                  </div>
                </>
              )
            }
          />
          <Route
            path={ROUTES.BIOGRAPHY}
            element={
              characterLoading ? (
                <div className="app__loading">
                  Loading character...
                </div>
              ) : currentCharacterId && character ? (
                <SectionOrderProvider userId={user.uid}>
                  <BiographyPage
                    character={character}
                    updateCharacterData={updateCharacterData}
                    disableAutoSave={disableAutoSave}
                    enableAutoSave={enableAutoSave}
                  />
                </SectionOrderProvider>
              ) : (
                <>
                  <Navigation />
                  <div className="app__error">
                    <p>No character selected. Please select a character first.</p>
                    <Button onClick={handleNavigateToCharacterSelect}>
                      Go to Character Select
                    </Button>
                  </div>
                </>
              )
            }
          />
          <Route
            path={ROUTES.CONTENT_LIBRARY}
            element={
              characterLoading ? (
                <div className="app__loading">
                  Loading character...
                </div>
              ) : currentCharacterId && character ? (
                <>
                  <Navigation />
                  <ContentLibrary
                    characterId={currentCharacterId || undefined}
                    character={character}
                    baseCharacter={baseCharacter}
                    onAddToCharacter={async (itemType, itemId) => {
                      if (itemType === 'spell') {
                        // NEW: Reference-based domain card addition (no transformation needed!)
                        if (isDuplicateDomainCard(itemId)) {
                          showToast.error('Domain card already added to character');
                          return; // Don't add duplicate
                        }

                        try {
                          const domainCard = await findDomainCard(itemId);
                          if (domainCard) {
                            // Add to loadout by default (following 5-card limit)
                            enhancedCharacter.addDomainCardToLoadout(itemId);
                            showToast.success(`Added domain card to loadout: ${domainCard.name}`);
                          } else {
                            showToast.error('Domain card not found');
                          }
                        } catch (error) {
                          showToast.error('Failed to add domain card');
                        }

                      } else if (itemType === 'equipment') {
                        // NEW: Reference-based equipment addition (no transformation needed!)
                        try {
                          const equipmentData = await findEquipment(itemId);
                          if (equipmentData) {
                            enhancedCharacter.addEquipmentByReference(itemId, 1);
                            showToast.equipment.added(equipmentData.name);
                          } else {
                            showToast.equipment.error('Equipment not found');
                          }
                        } catch (error) {
                          showToast.equipment.error('Failed to add equipment');
                        }
                      } else if (itemType === 'loot') {
                        // Loot item addition
                        try {
                          await itemLoader.initialize();
                          const item = itemLoader.findItemById(itemId);
                          if (item && !isConsumableItem(item)) {
                            enhancedCharacter.addLootByReference(itemId, 1);
                            showToast.equipment.added(item.name);
                          } else {
                            showToast.equipment.error('Item not found');
                          }
                        } catch (error) {
                          showToast.equipment.error('Failed to add loot item');
                        }
                      } else if (itemType === 'consumable') {
                        // Consumable item addition
                        try {
                          await itemLoader.initialize();
                          const item = itemLoader.findItemById(itemId);
                          if (item && isConsumableItem(item)) {
                            // Check max-5 limit before adding
                            const currentConsumableRefs = character?.inventory?.consumableReferences || [];
                            const currentCountBySubtype = currentConsumableRefs
                              .filter(ref => {
                                const refItem = itemLoader.findItemById(ref.itemId);
                                return refItem && isConsumableItem(refItem) && refItem.subtype === item.subtype;
                              })
                              .reduce((sum, ref) => sum + ref.quantity, 0);

                            if (currentCountBySubtype >= 5) {
                              showToast.error(`Maximum 5 ${item.subtype}s allowed`);
                              return;
                            }

                            enhancedCharacter.addConsumableByReference(itemId, 1);
                            showToast.equipment.added(item.name);
                          } else {
                            showToast.equipment.error('Item not found');
                          }
                        } catch (error) {
                          showToast.equipment.error('Failed to add consumable item');
                        }
                      }
                    }}
                    onRemoveFromCharacter={async (itemType, itemId) => {
                      if (itemType === 'spell') {
                        // Remove domain card from character vault/loadout
                        enhancedCharacter.removeDomainCard?.(itemId);
                        showToast.success('Domain card removed');
                      } else if (itemType === 'equipment') {
                        // NEW: Remove equipment by reference (removes all quantity)
                        enhancedCharacter.removeEquipmentByReference(itemId, 999); // Remove all
                        showToast.equipment.removed('Equipment');
                      }
                    }}
                  />
                </>
              ) : (
                <>
                  <Navigation />
                  <div className="app__error">
                    <p>No character selected. Please select a character first.</p>
                    <Button onClick={handleNavigateToCharacterSelect}>
                      Go to Character Select
                    </Button>
                  </div>
                </>
              )
            }
          />
          <Route
            path={ROUTES.DEVELOPER}
            element={
              <>
                <Navigation />
                <DeveloperPage />
              </>
            }
          />
          <Route
            path={ROUTES.LEGAL}
            element={
              <>
                <Navigation />
                <LegalPage />
              </>
            }
          />
        </Routes>
      </main>

      {/* Toast notifications */}
      <Toaster />

      {/* Footer with attribution */}
      <Footer />
    </div>
  );
};

// Main App component - wrapper for Router
const App: React.FC = () => {
  const { user, loading: authLoading } = useAuth();

  return (
    <ThemeProvider themes={availableThemes} defaultTheme="dark">
      {authLoading ? (
        <div className="app">
          <Header />
          <div className="app__loading">
            Loading...
          </div>
          <Footer />
        </div>
      ) : !user ? (
        <div className="app">
          <Header />
          <div className="app__auth">
            <AuthSection />
          </div>
          <Footer />
        </div>
      ) : (
        <Router>
          <AppContent user={user} authLoading={authLoading} />
        </Router>
      )}
    </ThemeProvider>
  );
};

export default App;